import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { SignalPriceInfo } from '@/components/common';
import { DeleteConfirmation } from '@/components/signals';
import { useSignals, useTelegram } from '@/hooks';
import { formatSignalValue, formatDate } from '@/utils/formatting';
import { SIGNAL_TYPE_LABELS, DIRECTION_LABELS } from '@/api/types';

export const SignalDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { signals, currentPrices, deleteSignal, toggleSignalActive, isLoading } = useSignals();
  const { showBackButton, hideBackButton, hideMainButton, impactOccurred, showConfirm } = useTelegram();

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const signal = useMemo(() => signals.find((s) => s.id === id), [signals, id]);
  const currentPrice = id ? currentPrices[id] : undefined;

  // Redirect if signal not found
  useEffect(() => {
    if (!signal && signals.length > 0) {
      navigate('/signals', { replace: true });
    }
  }, [signal, signals.length, navigate]);

  // Set up back button
  useEffect(() => {
    showBackButton(() => navigate('/signals'));
    hideMainButton();

    return () => {
      hideBackButton();
    };
  }, [showBackButton, hideBackButton, hideMainButton, navigate]);

  const handleEdit = () => {
    impactOccurred('medium');
    navigate(`/signals/${id}/edit`);
  };

  const handleToggleActive = async () => {
    if (!signal) return;
    impactOccurred('light');
    await toggleSignalActive(signal.id, !signal.active, signal.direction);
  };

  const handleDeleteClick = async () => {
    const confirmed = await showConfirm('Are you sure you want to delete this alert?');
    if (confirmed) {
      setShowDeleteModal(true);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!signal) return;

    setIsDeleting(true);
    const success = await deleteSignal(signal.id);
    setIsDeleting(false);

    if (success) {
      navigate('/signals', { replace: true });
    }
  };

  if (!signal) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'var(--tg-theme-bg-color)',
        }}
      >
        <p style={{ color: 'var(--tg-theme-hint-color)' }}>Loading...</p>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        backgroundColor: 'var(--tg-theme-bg-color)',
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '16px',
          borderBottom: '1px solid var(--tg-theme-secondary-bg-color)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <h1
          style={{
            fontSize: '20px',
            fontWeight: 700,
            color: 'var(--tg-theme-text-color)',
            margin: 0,
          }}
        >
          Alert Details
        </h1>
        <button
          onClick={handleEdit}
          style={{
            backgroundColor: 'transparent',
            border: 'none',
            color: 'var(--tg-theme-link-color)',
            fontSize: '16px',
            cursor: 'pointer',
            padding: '8px',
          }}
        >
          Edit
        </button>
      </div>

      {/* Signal Info */}
      <div style={{ padding: '16px' }}>
        {/* Symbol Card */}
        <div
          style={{
            backgroundColor: 'var(--tg-theme-secondary-bg-color)',
            borderRadius: '16px',
            padding: '20px',
            marginBottom: '16px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
            {signal.asset?.image && (
              <img
                src={signal.asset.image}
                alt={signal.symbol}
                style={{ width: '48px', height: '48px', borderRadius: '50%' }}
              />
            )}
            <div>
              <h2
                style={{
                  fontSize: '22px',
                  fontWeight: 700,
                  color: 'var(--tg-theme-text-color)',
                  margin: 0,
                }}
              >
                {signal.symbol}/{signal.quoteCurrency}
              </h2>
              <p
                style={{
                  fontSize: '14px',
                  color: 'var(--tg-theme-hint-color)',
                  margin: '4px 0 0',
                }}
              >
                {signal.asset?.name}
              </p>
            </div>
          </div>

          <SignalPriceInfo
            startPrice={signal.startPrice}
            currentPrice={currentPrice}
            currency={signal.quoteCurrency}
          />
        </div>

        {/* Details */}
        <div
          style={{
            backgroundColor: 'var(--tg-theme-secondary-bg-color)',
            borderRadius: '16px',
            overflow: 'hidden',
            marginBottom: '16px',
          }}
        >
          <DetailRow label="Type" value={SIGNAL_TYPE_LABELS[signal.type]} />
          <DetailRow
            label="Target"
            value={formatSignalValue(signal.value, signal.type, signal.quoteCurrency)}
          />
          {signal.direction !== 'none' && (
            <DetailRow label="Direction" value={DIRECTION_LABELS[signal.direction]} />
          )}
          <DetailRow label="Repeat" value={signal.repeat ? 'Yes' : 'No'} />
          <DetailRow label="Status" value={signal.active ? 'Active' : 'Paused'} />
          <DetailRow label="Created" value={formatDate(signal.createdAt)} isLast />
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
          <button
            onClick={handleToggleActive}
            disabled={isLoading}
            style={{
              flex: 1,
              padding: '14px',
              fontSize: '16px',
              fontWeight: 600,
              backgroundColor: signal.active
                ? 'rgba(255, 149, 0, 0.15)'
                : 'rgba(52, 199, 89, 0.15)',
              color: signal.active ? '#ff9500' : '#34c759',
              border: 'none',
              borderRadius: '12px',
              cursor: 'pointer',
            }}
          >
            {signal.active ? 'Pause' : 'Resume'}
          </button>
          <button
            onClick={handleDeleteClick}
            disabled={isLoading}
            style={{
              flex: 1,
              padding: '14px',
              fontSize: '16px',
              fontWeight: 600,
              backgroundColor: 'rgba(255, 59, 48, 0.15)',
              color: '#ff3b30',
              border: 'none',
              borderRadius: '12px',
              cursor: 'pointer',
            }}
          >
            Delete
          </button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmation
        signal={signal}
        isOpen={showDeleteModal}
        isLoading={isDeleting}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setShowDeleteModal(false)}
      />
    </div>
  );
};

interface DetailRowProps {
  label: string;
  value: string;
  isLast?: boolean;
}

const DetailRow: React.FC<DetailRowProps> = ({ label, value, isLast }) => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '14px 16px',
      borderBottom: isLast ? 'none' : '1px solid var(--tg-theme-bg-color)',
    }}
  >
    <span style={{ color: 'var(--tg-theme-hint-color)', fontSize: '15px' }}>{label}</span>
    <span style={{ color: 'var(--tg-theme-text-color)', fontSize: '15px', fontWeight: 500 }}>
      {value}
    </span>
  </div>
);
